import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { FAQS } from "@/data/faq";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";

const Item = ({ faq, i, open, onToggle }) => (
    <div className="overflow-hidden rounded-2xl border border-sand bg-cream" data-testid={`faq-item-${i}`}>
        <button
            data-testid={`faq-toggle-${i}`}
            onClick={onToggle}
            aria-expanded={open}
            className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors duration-300 hover:bg-bone/60"
        >
            <span className="font-serif text-xl font-medium text-ink">{faq.q}</span>
            <ChevronDown
                className={`h-5 w-5 shrink-0 text-terracotta transition-transform duration-300 ${open ? "rotate-180" : ""}`}
            />
        </button>
        <AnimatePresence initial={false}>
            {open && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                    <p className="px-6 pb-6 text-sm leading-relaxed text-moss" data-testid={`faq-answer-${i}`}>
                        {faq.a}
                    </p>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

export function FaqList({ items = FAQS }) {
    const [open, setOpen] = useState(0);
    return (
        <div className="flex flex-col gap-4">
            {items.map((f, i) => (
                <Reveal key={i} delay={i * 0.05}>
                    <Item faq={f} i={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
                </Reveal>
            ))}
        </div>
    );
}

export default function FAQ() {
    return (
        <section id="faq" data-testid="faq-section" className="bg-cream py-20 md:py-32">
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                <SectionHeading overline="FAQ" title="Questions, Answered Honestly" testId="faq-heading">
                    If anything is unclear, call us on 89781 33934 — we are happy to talk it through.
                </SectionHeading>

                <div className="mt-12">
                    <FaqList items={FAQS.slice(0, 3)} />
                </div>

                <Reveal delay={0.15}>
                    <Link
                        to="/faq"
                        data-testid="faq-view-all-link"
                        className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-forest transition-colors duration-300 hover:text-terracotta"
                    >
                        View all frequently asked questions
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}
